/**
 * PostProcessing — RenderPipeline with toon outline, bloom and FXAA.
 *
 * Four quality tiers with bidirectional FPS scaling:
 *   ultra:  pipeline outline + bloom + FXAA, pixelRatio 2.0,  shadows 2048
 *   high:   pipeline outline + bloom + FXAA, pixelRatio 1.5,  shadows 1024
 *   medium: HULL outline, FXAA, pixelRatio 1.25, no shadows
 *   low:    HULL outline, pixelRatio 1.0, no shadows
 *
 * Degrade: <30fps for 3s → drop one tier — but never when the frame rate is
 * sitting at the environment's own rAF cap (30Hz Low Power Mode, Energy Saver)
 * with an idle main thread; degrading cannot raise a cap. Recover: sustained
 * fps above min(55, 0.9 × cap) for 15s → raise one. The first 10s after boot
 * are ignored entirely — they are shader-compile hitches, not steady state.
 *
 * ── The mobile fix ──────────────────────────────────────────────────────────
 * v1 hard-capped mobile at `medium`, `medium` set outline:false and bloom:false,
 * and the pipeline was only built when `maxTier >= high` — so on a phone the
 * pipeline was NEVER CONSTRUCTED and the game lost the one thing that makes it
 * recognisable. Here `medium` and `low` still skip the post pass, but they emit
 * `outlineMode() === 'hull'`, and CheapOutline.js gives every character an
 * inverted-hull silhouette that costs one extra draw per character and no render
 * targets. A phone still reads as RWA Royale.
 *
 * Two crash lessons from v1 are preserved verbatim and must stay:
 *   - the pipeline is built ONCE and never rebuilt at runtime (:60-62 in v1)
 *   - shadow maps are toggled, never disposed at runtime (:149-151 in v1)
 */

import * as THREE from 'three/webgpu';
import { toonOutlinePass, renderOutput, uniform, mix, vec3, vec4, float, uv, smoothstep, luminance } from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { fxaa } from 'three/addons/tsl/display/FXAANode.js';

const TIER_ORDER = ['low', 'medium', 'high', 'ultra'];
const OUTLINE_COLOR = new THREE.Color(0x000000);

/**
 * `particleBudget` is CONCURRENT BURSTS, not particles.
 *
 * v1 returned 20 on ultra and 5 on low, and `spawnParticles` evicted the oldest
 * burst whenever it was full. With 8 players casting abilities that budget means
 * ability VFX delete each other mid-flight — the ultimate you just fired
 * disappears because someone else landed a basic. These numbers are ~3x v1's and
 * eviction now prefers bursts that are nearly finished (see ScreenEffects.js).
 */
const TIER_CONFIG = {
  ultra: { pixelRatio: 2.0, shadowSize: 2048, post: true, outline: 'pipeline', particleBudget: 64 },
  high: { pixelRatio: 1.5, shadowSize: 1024, post: true, outline: 'pipeline', particleBudget: 48 },
  medium: { pixelRatio: 1.25, shadowSize: 0, post: false, outline: 'hull', particleBudget: 32 },
  low: { pixelRatio: 1.0, shadowSize: 0, post: false, outline: 'hull', particleBudget: 18 },
};

let renderPipeline = null;
let currentTier = 'high';
let maxTier = 'ultra';
// `?tier=low|medium|high|ultra` pins the tier and turns the governor off —
// there is no other way to SEE what a weaker device gets without owning one.
const _tierOverride = (() => {
  const t = new URLSearchParams(window.location.search).get('tier');
  return TIER_CONFIG[t] ? t : null;
})();
let _renderer = null;
let _scene = null;
let _camera = null;
let _directionalLight = null;
const _tierListeners = new Set();

// Death grade: 1 = fully desaturated + edge-darkened. Lives in the pipeline
// permanently (built once, never rebuilt) and is a visual no-op at 0. On the
// hull tiers there is no post pass, so setDeathGrade is silently inert there —
// the death moment still reads through screen flash and shake.
const _deathGrade = uniform(0);
let _deathGradeLastDecay = 0;

let frameCount = 0;
let fpsCheckTime = 0;
let degradeSince = 0;
let recoverSince = 0;
const FPS_CHECK_INTERVAL = 1000;

// ── Environment awareness for the governor ──────────────────────────────────
// A capped rAF (macOS Low Power Mode, Chrome Energy Saver, 30Hz displays) pins
// measured fps at/below the degrade threshold forever. Without knowing the cap,
// the old governor slid every such machine high → medium → low permanently
// while the main thread was 90% idle.
let _bootAt = 0;
/** Best estimate of the environment's frame-rate ceiling (bare-rAF sample at
 *  boot, then raised to any fps we ever actually observe). 0 = unknown. */
let _rafCapFps = 0;
/** EMA of main-thread ms spent producing a frame (sim + render submission). */
let _cpuMsEma = 0;
const WARMUP_MS = 10000;

function _sampleRafCap() {
  const deltas = [];
  let last = 0;
  const tick = (t) => {
    if (last > 0) deltas.push(t - last);
    last = t;
    if (deltas.length < 24) {
      requestAnimationFrame(tick);
      return;
    }
    deltas.sort((a, b) => a - b);
    const median = deltas[deltas.length >> 1];
    if (median > 0) _rafCapFps = Math.max(_rafCapFps, 1000 / median);
  };
  requestAnimationFrame(tick);
}

function detectInitialTier() {
  const cores = navigator.hardwareConcurrency || 2;
  // DEVICE pixels, not CSS pixels: a DPR-2 Retina laptop reads 1.34M CSS pixels
  // and could never start at ultra while a 1080p DPR-1 tower could. DPR is
  // clamped to 2 to match the pixelRatio the tiers will actually render at.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const pixels = screen.width * screen.height * dpr * dpr;
  const mobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth < 820;

  if (mobile) {
    // A modern phone runs the real pipeline. v1 refused to let it try.
    maxTier = cores >= 6 ? 'high' : 'medium';
    return cores >= 6 ? 'medium' : 'low';
  }

  if (cores >= 8 && pixels >= 2_000_000) return 'ultra';
  if (cores >= 6) return 'high';
  if (cores >= 4) return 'medium';
  return 'low';
}

// Build the full effects pipeline once — never rebuild at runtime.
// Rebuilding disposes GPU textures (shadow maps, render targets) while the GPU
// may still reference them, causing "Destroyed texture" crashes.
function buildPipelineOnce() {
  renderPipeline = new THREE.RenderPipeline(_renderer);

  // FXAANode requires sRGB input — "tone mapping and color space conversion
  // must happen before the anti-aliasing" (r183 FXAANode docs). The pipeline's
  // default output transform appends them AFTER our chain, which left the AA
  // working on linear HDR and barely touching the black toon outlines this art
  // style lives on. So: disable the automatic transform and place
  // renderOutput() mid-chain ourselves.
  renderPipeline.outputColorTransform = false;

  // 0.006, not v1's 0.003: the thickness is clip-space (screen-proportional),
  // so 0.003 is ~1px at 1080p and FXAA erases it — v1 shipped an outline nobody
  // could see. 0.006 ≈ 0.3% of screen height, which is also the effective weight
  // CheapOutline.js produces on the hull tiers, so the silhouette no longer
  // changes weight when the governor crosses the pipeline/hull boundary.
  const scenePass = toonOutlinePass(_scene, _camera, OUTLINE_COLOR, 0.006, 1.0);
  const scenePassColor = scenePass.getTextureNode('output');
  // Bloom stays in linear HDR — thresholding after tone mapping would crush it.
  let outputNode = scenePassColor.add(bloom(scenePassColor, 0.35, 0.4, 0.55));
  // ACES + sRGB here (both resolved from the renderer via pipeline context)…
  outputNode = renderOutput(outputNode);
  // …so FXAA runs on display-referred sRGB, per its contract.
  outputNode = fxaa(outputNode);

  // Death grade, after AA (a per-pixel color transform doesn't disturb FXAA's
  // edge detection) and now genuinely display-referred, as its math intends:
  // pull toward dimmed luminance, darken toward the corners.
  const desat = mix(outputNode.rgb, vec3(luminance(outputNode.rgb)).mul(0.8), _deathGrade.mul(0.85));
  const edge = uv().sub(0.5).length();
  const vignette = float(1).sub(smoothstep(float(0.3), float(0.72), edge).mul(_deathGrade).mul(0.5));
  outputNode = vec4(desat.mul(vignette), outputNode.a);

  renderPipeline.outputNode = outputNode;
  renderPipeline.needsUpdate = true;
}

export function initPostProcessing(rendererRef, sceneRef, cameraRef, directionalLight) {
  _renderer = rendererRef;
  _scene = sceneRef;
  _camera = cameraRef;
  _directionalLight = directionalLight || null;

  currentTier = detectInitialTier();
  if (_tierOverride) {
    currentTier = _tierOverride;
    maxTier = _tierOverride;
  }
  fpsCheckTime = performance.now();
  _bootAt = fpsCheckTime;
  // Sample the environment's rAF cadence starting now, before the render loop
  // exists — at 60Hz this costs ~400ms of wall time and no boot delay.
  _sampleRafCap();

  // Built once, at boot, if this device could ever reach a tier that uses it.
  // Deferring it would mean building at runtime, which is the crash above.
  if (TIER_ORDER.indexOf(maxTier) >= TIER_ORDER.indexOf('high')) {
    buildPipelineOnce();
  }
  _renderer.setPixelRatio(Math.min(window.devicePixelRatio, TIER_CONFIG[currentTier].pixelRatio));
  applyShadowSettings(currentTier);

  console.log(`[PostProcess] quality=${currentTier} max=${maxTier} outline=${outlineMode()}`);
}

export function renderFrame() {
  const cpuStart = performance.now();
  if (renderPipeline && TIER_CONFIG[currentTier].post) {
    renderPipeline.render();
  } else {
    _renderer.render(_scene, _camera);
  }

  frameCount++;
  const now = performance.now();

  // Main-thread cost of the render submission — the governor's evidence for
  // "is low fps actually our fault". A capped-but-idle frame measures ~3-6ms.
  const cpuMs = now - cpuStart;
  _cpuMsEma = _cpuMsEma === 0 ? cpuMs : _cpuMsEma * 0.9 + cpuMs * 0.1;

  // Self-decaying so a single setDeathGrade(1) is a complete effect: full
  // grade fades out in ~450ms and never sticks if the caller forgets the 0.
  if (_deathGrade.value > 0) {
    const dt = _deathGradeLastDecay > 0 ? (now - _deathGradeLastDecay) / 1000 : 0;
    _deathGrade.value = Math.max(0, _deathGrade.value - dt / 0.45);
    _deathGradeLastDecay = _deathGrade.value > 0 ? now : 0;
  }

  if (now - fpsCheckTime > FPS_CHECK_INTERVAL) {
    const fps = (frameCount / (now - fpsCheckTime)) * 1000;
    frameCount = 0;
    fpsCheckTime = now;
    autoAdjustQuality(fps, now);
  }
}

function autoAdjustQuality(fps, now) {
  if (_tierOverride) return;
  // fps can never exceed the environment cap, so observing it proves the cap
  // is at least that high. This also self-corrects a boot sample taken during
  // compile hitches.
  _rafCapFps = Math.max(_rafCapFps, fps);

  // Boot is shader-compile hitches, not steady state; a slow-compiling machine
  // used to lose a tier before combat even started.
  if (now - _bootAt < WARMUP_MS) {
    degradeSince = 0;
    recoverSince = 0;
    return;
  }

  const tierIdx = TIER_ORDER.indexOf(currentTier);
  const cap = _rafCapFps || 60;
  // "This is the environment's ceiling, not our floor": fps is pinned at the
  // cap AND the main thread is mostly idle. Only trusted for caps ≥28 — a
  // machine that has never exceeded 20fps is genuinely struggling, not capped
  // (real rAF caps in the wild are 30/60/120Hz).
  const frameBudgetMs = 1000 / Math.max(fps, 1);
  const atCap = cap >= 28 && fps >= cap * 0.85;
  const cpuIdle = _cpuMsEma < frameBudgetMs * 0.6;
  // Recovery must be reachable UNDER the cap: the old absolute >55fps made
  // every degrade permanent on a 30Hz-capped machine (max observable 30).
  const recoverAt = Math.min(55, cap * 0.9);

  if (fps < 30 && !(atCap && cpuIdle)) {
    if (degradeSince === 0) degradeSince = now;
    recoverSince = 0;
    if (now - degradeSince >= 3000 && tierIdx > 0) {
      degradeSince = 0;
      applyTier(TIER_ORDER[tierIdx - 1]);
    }
  } else if (fps > recoverAt) {
    if (recoverSince === 0) recoverSince = now;
    degradeSince = 0;
    const maxIdx = TIER_ORDER.indexOf(maxTier);
    if (now - recoverSince >= 15000 && tierIdx < maxIdx) {
      recoverSince = 0;
      applyTier(TIER_ORDER[tierIdx + 1]);
    }
  } else {
    degradeSince = 0;
    recoverSince = 0;
  }
}

function applyTier(tier) {
  if (tier === currentTier) return;
  const before = outlineMode();
  currentTier = tier;
  console.log(`[PostProcess] quality=${tier}`);

  _renderer.setPixelRatio(Math.min(window.devicePixelRatio, TIER_CONFIG[tier].pixelRatio));
  applyShadowSettings(tier);

  // Crossing the pipeline/hull boundary changes who draws the silhouette. Hulls
  // are added and removed per object, so the listener does that work rather than
  // this module reaching into the scene graph.
  const after = outlineMode();
  if (after !== before) {
    for (const fn of _tierListeners) fn(after);
  }
}

// Only toggle castShadow — never dispose shadow maps at runtime. Disposing GPU
// textures while the pipeline still references them causes
// "Destroyed texture [ShadowDepthTexture]" WebGPU crashes.
function applyShadowSettings(tier) {
  if (_directionalLight) {
    _directionalLight.castShadow = TIER_CONFIG[tier].shadowSize > 0;
  }
}

/**
 * Death desaturation moment. Call with 1 on elimination; it ramps back to 0 on
 * its own over ~450ms. No-op on tiers without the post pass.
 */
export function setDeathGrade(t) {
  _deathGrade.value = Math.max(0, Math.min(1, t ?? 0));
  _deathGradeLastDecay = _deathGrade.value > 0 ? performance.now() : 0;
}

export function getParticleBudget() {
  return TIER_CONFIG[currentTier].particleBudget;
}

export function getCurrentTier() {
  return currentTier;
}

/** 'pipeline' when the post pass draws outlines, 'hull' when the meshes must. */
export function outlineMode() {
  return TIER_CONFIG[currentTier].outline;
}

/** Called with the new mode whenever the outline owner changes. */
export function onOutlineModeChange(fn) {
  _tierListeners.add(fn);
  return () => _tierListeners.delete(fn);
}

export function setMaxTier(tier) {
  if (TIER_CONFIG[tier]) maxTier = tier;
}
